import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import api from "@/lib/api";
import { Users, Plus, ArrowRight, Archive, ArchiveRestore } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import {
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";

const VIEW = { ACTIVE: "active", ARCHIVED: "archived" };

export default function Patients() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", age: "", sex: "", email: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState(VIEW.ACTIVE);
  const [busy, setBusy] = useState(false);

  const isArchived = view === VIEW.ARCHIVED;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/patients", { params: { archived: isArchived } });
      setPatients(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/patients", {
        name: form.name,
        age: form.age ? Number(form.age) : null,
        sex: form.sex || null,
        email: form.email || null,
        notes: form.notes || null,
      });
      toast.success("Patient added");
      setForm({ name: "", age: "", sex: "", email: "", notes: "" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add patient");
    } finally {
      setSaving(false);
    }
  };

  const archivePatient = async (patientId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm("Archive this patient? Their reports stay searchable on the dashboard archive.")) return;
    setBusy(true);
    try {
      await api.post("/patients/bulk-archive", { ids: [patientId], archived: true });
      toast.success("Patient archived");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Archive failed");
    } finally { setBusy(false); }
  };

  const restorePatient = async (patientId, e) => {
    e.preventDefault();
    e.stopPropagation();
    setBusy(true);
    try {
      await api.post("/patients/bulk-archive", { ids: [patientId], archived: false });
      toast.success("Patient restored");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed");
    } finally { setBusy(false); }
  };

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Records</div>
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <h1 className="font-display text-3xl md:text-4xl font-semibold">Patients</h1>
            <ViewToggle value={view} onChange={setView} testidPrefix="patients" />
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="patients-add-button" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-white font-medium" style={{ background: "var(--color-primary)" }}>
              <Plus className="w-4 h-4" /> Add patient
            </button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">New patient</DialogTitle>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Full name</label>
                <input data-testid="patient-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 w-full border bg-white rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#2A4736]" style={{ borderColor: "var(--color-border)" }} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Age</label>
                  <input data-testid="patient-form-age" type="number" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="mt-1.5 w-full border bg-white rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#2A4736]" style={{ borderColor: "var(--color-border)" }} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Sex</label>
                  <select data-testid="patient-form-sex" value={form.sex} onChange={(e) => setForm({ ...form, sex: e.target.value })} className="mt-1.5 w-full border bg-white rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#2A4736]" style={{ borderColor: "var(--color-border)" }}>
                    <option value="">—</option>
                    <option value="Female">Female</option>
                    <option value="Male">Male</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Email (optional)</label>
                <input data-testid="patient-form-email" type="email" placeholder="patient@example.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 w-full border bg-white rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#2A4736]" style={{ borderColor: "var(--color-border)" }} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest" style={{ color: "var(--color-text-secondary)" }}>Notes (optional)</label>
                <textarea data-testid="patient-form-notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 w-full border bg-white rounded-md px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#2A4736]" style={{ borderColor: "var(--color-border)" }} />
              </div>
              <DialogFooter>
                <button data-testid="patient-form-submit" type="submit" disabled={saving} className="px-5 py-2.5 rounded-md text-white font-medium disabled:opacity-60" style={{ background: "var(--color-primary)" }}>
                  {saving ? "Saving…" : "Add patient"}
                </button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="p-10 text-center"><div className="spinner mx-auto" /></div>
      ) : patients.length === 0 ? (
        <div className="bg-white border rounded-lg p-12 text-center" style={{ borderColor: "var(--color-border)" }}>
          {isArchived ? (
            <>
              <Archive className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--color-secondary)" }} />
              <p className="font-display text-lg">No archived patients</p>
              <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>Patients you archive will appear here and can be restored anytime.</p>
            </>
          ) : (
            <>
              <Users className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--color-secondary)" }} />
              <p className="font-display text-lg">No patients yet</p>
              <p className="text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>Add your first patient to start uploading reports.</p>
            </>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {patients.map((p) => (
            <Link
              key={p.patient_id}
              data-testid={`patient-card-${p.patient_id}`}
              to={`/patients/${p.patient_id}`}
              className="relative bg-white border rounded-lg p-6 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              style={{ borderColor: "var(--color-border)" }}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-xl font-semibold" style={{ color: "var(--color-text-primary)" }}>{p.name}</div>
                  <div className="text-xs mt-1" style={{ color: "var(--color-text-secondary)" }}>
                    {p.age ? `${p.age} yrs` : ""}{p.age && p.sex ? " · " : ""}{p.sex || ""}
                  </div>
                  {isArchived && p.archived_at && (
                    <div className="text-[11px] mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ background: "var(--bg-canvas)", color: "var(--color-text-secondary)" }}>
                      <Archive className="w-3 h-3" /> archived {format(new Date(p.archived_at), "d MMM yyyy")}
                    </div>
                  )}
                </div>
                <ArrowRight className="w-4 h-4" style={{ color: "var(--color-secondary)" }} />
              </div>
              {p.notes && <p className="text-sm mt-4 line-clamp-2" style={{ color: "var(--color-text-secondary)" }}>{p.notes}</p>}
              <div className="mt-5 flex items-center gap-2">
                {isArchived ? (
                  <button
                    data-testid={`patient-restore-${p.patient_id}`}
                    onClick={(e) => restorePatient(p.patient_id, e)}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium hover:bg-[#F9F8F6] disabled:opacity-60"
                    style={{ borderColor: "var(--color-border)", color: "var(--color-text-primary)" }}
                  >
                    <ArchiveRestore className="w-3.5 h-3.5" /> Restore
                  </button>
                ) : (
                  <button
                    data-testid={`patient-archive-${p.patient_id}`}
                    onClick={(e) => archivePatient(p.patient_id, e)}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs font-medium hover:bg-[#F9F8F6] disabled:opacity-60"
                    style={{ borderColor: "var(--color-border)", color: "var(--color-text-secondary)" }}
                  >
                    <Archive className="w-3.5 h-3.5" /> Archive
                  </button>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </Layout>
  );
}

function ViewToggle({ value, onChange, testidPrefix }) {
  const base = "px-3 py-1 text-xs font-medium rounded-md transition";
  return (
    <div className="inline-flex items-center p-0.5 border rounded-lg" style={{ borderColor: "var(--color-border)", background: "var(--bg-canvas)" }}>
      <button
        data-testid={`${testidPrefix}-view-active`}
        onClick={() => onChange(VIEW.ACTIVE)}
        className={base}
        style={{
          background: value === VIEW.ACTIVE ? "var(--bg-surface)" : "transparent",
          color: value === VIEW.ACTIVE ? "var(--color-text-primary)" : "var(--color-text-secondary)",
          boxShadow: value === VIEW.ACTIVE ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
        }}
      >
        Active
      </button>
      <button
        data-testid={`${testidPrefix}-view-archived`}
        onClick={() => onChange(VIEW.ARCHIVED)}
        className={base}
        style={{
          background: value === VIEW.ARCHIVED ? "var(--bg-surface)" : "transparent",
          color: value === VIEW.ARCHIVED ? "var(--color-text-primary)" : "var(--color-text-secondary)",
          boxShadow: value === VIEW.ARCHIVED ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
        }}
      >
        Archived
      </button>
    </div>
  );
}
